// src/components/issues/IssueCard.jsx
import React, { useState, useRef } from "react";
import { Card } from "components/ui/card";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Clock, User, Link2, CheckCircle2, Pause, Send, Eye } from "lucide-react";
import { beginFeedback } from "../../lib/interactionFeedback";
import SampleBadge from "components/guide/SampleBadge";

const STATUS = {
  new: { tone: "accent", label: "New" },
  triaged: { tone: "warning", label: "Triaged" },
  resolved: { tone: "success", label: "Resolved" },
};

function formatWhen(value) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// A Jira key such as "PAY-142" is worth showing; internal record IDs and Slack timestamps are not.
function readableKey(issue) {
  const key = String(issue.external_id || issue.jira_key || "").replace(/^(jira:)+/i, "");
  return /^[A-Z][A-Z0-9_]*-\d+$/.test(key) ? key : null;
}

// Slack issues keep their channel ID in different places depending on how they were imported.
const SLACK_CHANNEL_ID = /^[CGD][A-Z0-9]{6,}$/;
export function slackChannelId(issue) {
  if (!/slack/i.test(issue.source || "")) return null;
  const labels = [...(issue.labels || []), ...(issue.tags || [])];
  return issue.metadata?.slack_channel || issue.source_metadata?.channel || labels.find((l) => SLACK_CHANNEL_ID.test(l)) || null;
}

// onUpdate(id, patch) must be stable; the card is memoized so big boards stay responsive.
// channelNames maps Slack channel IDs to names; unknown channels show as "Slack channel".
function IssueCard({ issue, onUpdate, channelNames }) {
  const status = STATUS[issue.status] || STATUS.new;
  const key = readableKey(issue);
  const channelId = slackChannelId(issue);
  const channelName = channelId ? channelNames?.get(channelId) : null;
  const tags = (Array.isArray(issue.tags) ? issue.tags : []).filter((t) => t !== channelId);
  const [delegating, setDelegating] = useState(false);
  const [assignee, setAssignee] = useState(issue.assignee || "");
  const when = formatWhen(issue.updated_date || issue.created_date || issue.updated_at || issue.created_at);

  const [resolving, setResolving] = useState(false);
  const saving = useRef(false);
  const resolve = async event => {
    if (!onUpdate || saving.current) return;
    const feedback = beginFeedback(event, 'resolved');
    saving.current = true; setResolving(true);
    try { await onUpdate(issue.id, { status: "resolved" }, feedback, issue.title); }
    finally { saving.current = false; setResolving(false); }
  };
  const triage = () => onUpdate?.(issue.id, { status: "triaged" });
  const snooze = () => {
    // simple snooze mark via tag; replace later with timestamped snooze logic
    const tags = Array.isArray(issue.tags) ? issue.tags.slice() : [];
    if (!tags.includes("snoozed")) tags.push("snoozed");
    onUpdate?.(issue.id, { tags });
  };

  const delegate = () => {
    if (!assignee.trim()) return;
    onUpdate?.(issue.id, { assignee: assignee.trim(), status: issue.status === "new" ? "triaged" : issue.status });
    setDelegating(false);
  };

  return (
    <Card className="issue-card interactive" style={{ padding: 12, display: "grid", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <h3 style={{ margin: 0, fontFamily: "var(--font-sans)", fontSize: 14, fontWeight: 600, lineHeight: 1.35, minWidth: 0, letterSpacing: 0 }} title={issue.title}>
          {issue.title} <SampleBadge id={issue.id}/>
        </h3>
        <Badge variant={status.tone} className="badge-dot">{status.label}</Badge>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 11, color: "var(--fg-muted)" }}>
        {key && (issue.external_url
          ? <a href={issue.external_url} target="_blank" rel="noreferrer" title="Open in Jira"><Badge style={{ fontFamily: "var(--font-mono)" }}>{key}</Badge></a>
          : <Badge style={{ fontFamily: "var(--font-mono)" }}>{key}</Badge>)}
        {issue.source && <Badge>{issue.source}</Badge>}
        {channelId && <Badge variant="info" title={channelName ? `Slack channel #${channelName}` : "Slack channel"}>{channelName ? `#${channelName}` : "Slack channel"}</Badge>}
        {!!issue.impact && <Badge variant="warning">Impact {issue.impact}/5</Badge>}
        {when && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Clock size={11} /> {when}</span>}
        {issue.related_problem_id && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Link2 size={11} /> Problem #{issue.related_problem_id}
          </span>
        )}
      </div>

      {typeof issue.summary === "string" && issue.summary && (
        <p style={{ margin: 0, color: "var(--fg-muted)", fontSize: 12, lineHeight: 1.45 }}>{issue.summary}</p>
      )}

      {tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {tags.map((t) => <Badge key={`${issue.id}-${t}`}>#{t}</Badge>)}
        </div>
      )}

      {delegating ? (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Input
            aria-label="Assignee"
            placeholder="Assignee"
            value={assignee}
            autoFocus
            onChange={(e) => setAssignee(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && delegate()}
            style={{ flex: 1, minWidth: 0 }}
          />
          <Button variant="primary" size="sm" onClick={delegate}>Assign</Button>
          <Button variant="ghost" size="sm" onClick={() => { setDelegating(false); setAssignee(issue.assignee || ""); }}>Cancel</Button>
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 4, alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setDelegating(true)}
          title="Delegate"
          style={{ paddingLeft: 6 }}
        >
          <User size={13} />
          {issue.assignee ? issue.assignee : "Unassigned"}
          <Send size={12} style={{ opacity: 0.6 }} />
        </button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 2 }}>
          {issue.status !== "triaged" && issue.status !== "resolved" && (
            <Button variant="ghost" size="sm" onClick={triage} title="Mark as triaged">
              <Eye size={13} />
              Triage
            </Button>
          )}
          {issue.status !== "resolved" && (
            <Button variant="ghost" size="sm" onClick={resolve} disabled={resolving} aria-busy={resolving} title="Resolve" style={{ color: "var(--success-fg)" }}>
              <CheckCircle2 size={13} />
              {resolving ? "Resolving…" : "Resolve"}
            </Button>
          )}
          <Button variant="ghost" size="sm" className="btn-icon" onClick={snooze} title="Snooze" aria-label="Snooze">
            <Pause size={13} />
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default React.memo(IssueCard);
